import NextImage from 'next/legacy/image'
import { useRouter } from 'next/router'
import { useEffect, useRef, type ChangeEventHandler, type FunctionComponent } from 'react'
import { useQuickSearch } from '../../../hooks/useQuickSearch'
import { InternalLink } from '../../const/Links'
import { Translation, useTranslate } from '../../contexts/TranslationContext'
import SvgCross from '../../svg/icons/Cross'
import { useAuth } from '../../user/UserContext'
import { ProductSearchCard } from '../product/ProductSearchCard'
import { RegisterSearchBanner } from '../static/RegisterSearchBanner'
import { BrandCard } from './BrandCard'
import { BrandCardGroup } from './BrandCardGroup'
import { Button } from './Button'
import { Modal } from './Modal'
import { SearchCard } from './SearchCard'
import style from './SearchModal.module.sass'

type SearchModalProps = {
	searchedText: string
	closeModal: () => void
	onChange: ChangeEventHandler<HTMLInputElement>
	isOpen: boolean
}

export const SearchModal: FunctionComponent<SearchModalProps> = ({
	searchedText,
	closeModal,
	onChange,
	isOpen = false,
}) => {
	const searchResults = useQuickSearch(searchedText)
	const searchResultsAmount = searchResults.data?.total ?? 0
	const noResults = searchResultsAmount === 0

	const translate = useTranslate()
	const router = useRouter()
	const auth = useAuth()

	const goToResults = () => {
		router.push({ pathname: `${InternalLink.SearchPage}/`, query: { search: searchedText } })
		closeModal()
	}

	const inputRef = useRef<HTMLInputElement>(null)

	useEffect(() => {
		inputRef.current?.focus()
		if (!isOpen) {
			inputRef.current?.blur()
		}
	}, [inputRef, isOpen])
	return (
		<Modal close={closeModal} type="search" isOpen={isOpen} rendered onlyHiddenWithInput={!isOpen}>
			<div className={style.searchBar}>
				<div className={style.inputWrapper}>
					<input
						type="text"
						ref={inputRef}
						value={searchedText}
						className={style.input}
						placeholder={translate('searchModal.input.placeholder')}
						onChange={onChange}
						onKeyUp={(e) => {
							if (e.key === 'Enter') {
								goToResults()
							}
						}}
					/>
					<div className={style.buttonWrapper}>
						<Button
							label={<SvgCross />}
							type={{ variant: 'transparent' }}
							icon={{ aloneStanding: true, fillPrimary: true }}
							onClick={closeModal}
							ariaLabel={translate('searchModal.delete.button')}
						/>
					</div>
				</div>
			</div>
			{searchedText && searchedText.length > 2 && (
				<>
					{!!searchResults.data?.results && (
						<div className={style.resultsWrapper}>
							{!!searchResults.data.results.product.found && (
								<div className={style.block}>
									<div className={style.title}>
										<Translation tr="searchModal.products.title" />
									</div>
									<div className={style.brandsWrapper}>
										{searchResults.data.results.product.hits?.map((product) => (
											<ProductSearchCard
												key={product.id}
												title={
													product.productLine
														? `${product.brand?.name} ${product.productLine.name}`
														: product.brand?.name
												}
												specification={product.name}
												url={`${InternalLink.Product}${product.slug}`}
												imageUrl={product.heurekaImage?.url ?? product.image?.url}
												closeModal={closeModal}
											/>
										))}
									</div>
								</div>
							)}
							{!!searchResults.data.results.brand.found && (
								<div className={style.block}>
									<div className={style.title}>
										<Translation tr="searchModal.brands.title" />
									</div>
									<div className={style.brandsWrapper}>
										<BrandCardGroup
											brands={searchResults.data.results.brand.hits ?? []}
											isBrandNameVisible
											closeModal={closeModal}
										/>
									</div>
								</div>
							)}
							{!!searchResults.data.results.blogPost.found && (
								<div className={style.block}>
									<div className={style.title}>
										<Translation tr="searchModal.blogPosts.title" />
									</div>
									<div className={style.cardsWrapper}>
										{searchResults.data.results.blogPost.hits?.map((blogPost) => (
											<SearchCard
												key={blogPost.id}
												name={blogPost.title}
												url={`${InternalLink.BlogPost}${blogPost.slug}`}
												imageUrl={blogPost.image?.url}
												close={closeModal}
												type="blogPost"
											/>
										))}
									</div>
								</div>
							)}
							{!!searchResults.data.results.item.found && (
								<div className={style.block}>
									<div className={style.title}>
										<Translation tr="searchModal.items.title" />
									</div>
									<div className={style.cardsWrapper}>
										{searchResults.data.results.item.hits?.map((ingredient) => (
											<SearchCard
												key={ingredient.id}
												name={ingredient.name}
												url={`${InternalLink.Ingredient}${ingredient.slug}`}
												imageUrl={ingredient.image?.url}
												close={closeModal}
												type="item"
											/>
										))}
									</div>
								</div>
							)}
							{searchResults.isLoading && (
								<div className={style.loaderWrapper}>
									<NextImage
										src="/images/loader.png"
										alt={translate('loader.alt.image')}
										title={translate('loader.alt.image')}
										height={46}
										width={78}
									/>
								</div>
							)}
							{!noResults && !searchResults.isLoading && (
								<div className={style.resultsButtonWrapper}>
									<Button
										onClick={goToResults}
										label={`${translate('searchModal.allResults')} (${searchResultsAmount})`}
									/>
								</div>
							)}
						</div>
					)}
					{noResults && searchedText && (
						<div className={style.resultsWrapper}>
							<div className={style.noResults}>
								<Translation tr="searchModal.emptyState.title" />
							</div>
							<div className={style.brandGroupWrapper}>
								<div className={style.brandButtonWrapper}>
									<Button
										type={{ variant: 'underlinedGrey' }}
										label={translate('favouriteBrands.button.more')}
										url={InternalLink.Brands}
									/>
								</div>
								<div className={style.brandGroup}>
									{searchResults.data?.results.promotedBrands.map((brand) => (
										<BrandCard key={brand.id} brand={brand} />
									))}
								</div>
							</div>
							{auth.status !== 'signedIn' && (
								<div className={style.bannerWrapper}>
									<RegisterSearchBanner />
								</div>
							)}
						</div>
					)}
				</>
			)}
		</Modal>
	)
}
